import type { NovelItem } from '@app/_proto/Protos/novels';
import {
    GetRecommendationsRequest,
    type GetRecommendationsResponse,
    RecommendationField_FieldType,
} from '@app/_proto/Protos/recommendations';
import { RecommendationsClient } from '@app/_proto/Protos/recommendations.client';
import type { ContentWithGenre } from '@app/domains/home/types';
import { type GrpcApiOptions, useGrpcApiWithQuery } from '@app/libs/api';
import { useAuth } from '@app/libs/auth';
import { getOneofValue, useGrpcRequest } from '@app/libs/grpc';
import { useMemo } from 'react';

type UseRecommendationsParam = {
    contentType?: 'novel';
};

type UseRecommendationsReturn = {
    relatedContent: NovelItem | undefined;
    contents: NovelItem[] | undefined;
    contentsWithGenre: ContentWithGenre[] | undefined;
    isSuccess: boolean;
};

export default function useRecommendations(
    { contentType = 'novel' }: UseRecommendationsParam,
    options?: GrpcApiOptions<GetRecommendationsRequest, GetRecommendationsResponse>
): UseRecommendationsReturn {
    const { user } = useAuth();
    const recommendationsRequest = useGrpcRequest(GetRecommendationsRequest, {});

    const { data, isSuccess } = useGrpcApiWithQuery(
        RecommendationsClient,
        c => c.getRecommendations,
        recommendationsRequest,
        ['recommendations', user?.id],
        {
            ...options,
            cacheTime: 1000 * 60 * 60 * 24, // 24 hours cache
            refetchOnWindowFocus: false,
            staleTime: 0,
        }
    );

    const contentsWithGenre = useMemo(() => {
        return data?.items.map(r => ({
            content: getOneofValue(r.series?.item, contentType),
            genre: r.byFields.find(byField => byField.type === RecommendationField_FieldType.Genre)?.value,
        }));
    }, [contentType, data?.items]);

    const contents = contentsWithGenre?.map(contentWithGenre => contentWithGenre.content!);

    return {
        relatedContent: getOneofValue(data?.relatedTitle?.item, contentType),
        contents,
        contentsWithGenre,
        isSuccess,
    };
}
